import React, { useState, useEffect } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import TimeSlotsPicker, { TimeSlotInput } from '@/components/meetings/TimeSlotsPicker'
import RecurringPatternPicker from '@/components/meetings/RecurringPatternPicker'
import { RecurringPatternValue, DEFAULT_RECURRING_PATTERN } from '@/types/meeting'
import { meetingService } from '@/services/meetingService'
import { matchingService } from '@/services/matchingService'
import { planService } from '@/services/planService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import { Relationship } from '@/types/matching'
import { Plan } from '@/types/plan'
import {
  MeetingRequest,
  MeetingType,
  MeetingPlatform,
  CreateMeetingRequestData
} from '@/types/meeting'

interface MeetingRequestModalProps {
  isOpen: boolean
  onClose: () => void
  onMeetingRequested: (request: MeetingRequest) => void
  preselectedRecipientId?: string
}

const MEETING_TYPES: { value: MeetingType; label: string }[] = [
  { value: 'onboarding', label: 'Onboarding' },
  { value: 'regular_checkin', label: 'Regular Check-in' },
  { value: 'project_review', label: 'Project Review' },
  { value: 'career_guidance', label: 'Career Guidance' },
  { value: 'skill_assessment', label: 'Skill Assessment' },
  { value: 'goal_setting', label: 'Goal Setting' },
  { value: 'feedback_session', label: 'Feedback Session' },
  { value: 'other', label: 'Other' }
]

const MEETING_PLATFORMS: { value: MeetingPlatform; label: string }[] = [
  { value: 'zoom', label: 'Zoom' },
  { value: 'google_meet', label: 'Google Meet' },
  { value: 'microsoft_teams', label: 'Microsoft Teams' },
  { value: 'skype', label: 'Skype' },
  { value: 'in_person', label: 'In Person' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' }
]

interface AgendaItemInput {
  title: string
  description: string
  estimatedMinutes: number | ''
}

interface ActionItemInput {
  title: string
  description: string
  assignedTo: 'mentor' | 'mentee'
  dueDate: string
}

const MAX_TIME_SLOTS = 5

const MeetingRequestModal: React.FC<MeetingRequestModalProps> = ({
  isOpen,
  onClose,
  onMeetingRequested,
  preselectedRecipientId
}) => {
  const { user } = useAuth()
  const { showToast } = useToast()

  const [step, setStep] = useState(1)

  // Step 1
  const [relationships, setRelationships] = useState<Relationship[]>([])
  const [loadingRelationships, setLoadingRelationships] = useState(false)
  const [recipientId, setRecipientId] = useState('')
  const [type, setType] = useState<MeetingType>('regular_checkin')
  const [platform, setPlatform] = useState<MeetingPlatform>('zoom')
  const [meetingUrl, setMeetingUrl] = useState('')
  const [duration, setDuration] = useState<number>(30)
  const [message, setMessage] = useState('')
  const [plans, setPlans] = useState<Plan[]>([])
  const [planId, setPlanId] = useState('')

  // Step 2
  const [timeSlots, setTimeSlots] = useState<TimeSlotInput[]>([{ start: '', end: '' }])

  // Step 3
  const [agendaItems, setAgendaItems] = useState<AgendaItemInput[]>([
    { title: '', description: '', estimatedMinutes: '' }
  ])

  // Step 4
  const [actionItems, setActionItems] = useState<ActionItemInput[]>([
    { title: '', description: '', assignedTo: 'mentor', dueDate: '' }
  ])
  const [recurring, setRecurring] = useState<RecurringPatternValue>(DEFAULT_RECURRING_PATTERN)

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isOpen) {
      loadRelationships()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (preselectedRecipientId) {
      setRecipientId(preselectedRecipientId)
    }
  }, [preselectedRecipientId])

  useEffect(() => {
    setPlanId('')
    if (!recipientId) {
      setPlans([])
      return
    }
    const relationship = relationships.find(r => counterpartFor(r)?.id === recipientId)
    if (!relationship) {
      setPlans([])
      return
    }
    planService
      .getPlansForRelationship(relationship.id)
      .then(data => setPlans(data.filter(p => p.status === 'active')))
      .catch(() => setPlans([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recipientId, relationships])

  const loadRelationships = async () => {
    try {
      setLoadingRelationships(true)
      const data = await matchingService.getMyRelationships()
      setRelationships(data.filter(r => r.status === 'active'))
    } catch (error) {
      showToast({ type: 'error', message: 'Failed to load your active relationships' })
    } finally {
      setLoadingRelationships(false)
    }
  }

  const counterpartFor = (relationship: Relationship) =>
    user?.role === 'mentor' ? relationship.mentee : relationship.mentor

  const validateStep1 = (): boolean => {
    const newErrors: Record<string, string> = {}
    if (!recipientId) newErrors.recipientId = 'Please choose a recipient'
    if (!duration || duration < 5) newErrors.duration = 'Duration must be at least 5 minutes'
    if (meetingUrl && !/^https?:\/\//.test(meetingUrl)) {
      newErrors.meetingUrl = 'Meeting URL must start with http:// or https://'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = (): boolean => {
    const newErrors: Record<string, string> = {}
    const validSlots = timeSlots.filter(s => s.start && s.end)
    if (validSlots.length === 0) {
      newErrors.timeSlots = 'Propose at least one time slot'
    } else if (validSlots.some(s => new Date(s.end) <= new Date(s.start))) {
      newErrors.timeSlots = 'Each slot\'s end time must be after its start time'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep3 = (): boolean => {
    const newErrors: Record<string, string> = {}
    const validItems = agendaItems.filter(a => a.title.trim())
    if (validItems.length === 0) {
      newErrors.agenda = 'Add at least one agenda item'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep4 = (): boolean => {
    const newErrors: Record<string, string> = {}
    const validItems = actionItems.filter(a => a.title.trim())
    if (validItems.length === 0) {
      newErrors.actionItems = 'Add at least one action item'
    }
    if (recurring.enabled) {
      if (recurring.endMode === 'date' && !recurring.endDate) {
        newErrors.recurring = 'Choose an end date for the recurring series'
      } else if (recurring.endMode === 'occurrences' && !recurring.maxOccurrences) {
        newErrors.recurring = 'Choose a number of occurrences for the recurring series'
      }
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = () => {
    const validators = [validateStep1, validateStep2, validateStep3]
    if (validators[step - 1]()) {
      setErrors({})
      setStep(step + 1)
    }
  }

  const handleBack = () => {
    setErrors({})
    setStep(step - 1)
  }

  const handleAddAgendaItem = () => {
    setAgendaItems([...agendaItems, { title: '', description: '', estimatedMinutes: '' }])
  }

  const handleUpdateAgendaItem = (index: number, field: keyof AgendaItemInput, value: string | number) => {
    const updated = [...agendaItems]
    updated[index] = { ...updated[index], [field]: value }
    setAgendaItems(updated)
  }

  const handleRemoveAgendaItem = (index: number) => {
    setAgendaItems(agendaItems.filter((_, i) => i !== index))
  }

  const handleAddActionItem = () => {
    setActionItems([...actionItems, { title: '', description: '', assignedTo: 'mentor', dueDate: '' }])
  }

  const handleUpdateActionItem = (
    index: number,
    field: keyof ActionItemInput,
    value: string
  ) => {
    const updated = [...actionItems]
    updated[index] = { ...updated[index], [field]: value } as ActionItemInput
    setActionItems(updated)
  }

  const handleRemoveActionItem = (index: number) => {
    setActionItems(actionItems.filter((_, i) => i !== index))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateStep4()) return

    setIsSubmitting(true)

    try {
      const data: CreateMeetingRequestData = {
        recipientId,
        proposedTimes: timeSlots
          .filter(s => s.start && s.end)
          .map(s => ({ start: new Date(s.start).toISOString(), end: new Date(s.end).toISOString() })),
        duration,
        type,
        platform,
        meetingUrl: meetingUrl || undefined,
        message: message || undefined,
        agenda: agendaItems
          .filter(a => a.title.trim())
          .map((a, i) => ({
            title: a.title,
            description: a.description || undefined,
            estimatedMinutes: Number(a.estimatedMinutes) || 15,
            order: i
          })),
        actionItems: actionItems
          .filter(a => a.title.trim())
          .map(a => ({
            title: a.title,
            description: a.description || undefined,
            assignedTo: a.assignedTo,
            dueDate: a.dueDate || undefined
          })),
        isRecurring: recurring.enabled,
        recurringPattern: recurring.enabled
          ? {
              frequency: recurring.frequency,
              interval: recurring.interval,
              daysOfWeek: recurring.daysOfWeek.length > 0 ? recurring.daysOfWeek : undefined,
              endDate:
                recurring.endMode === 'date' && recurring.endDate
                  ? new Date(recurring.endDate).toISOString()
                  : undefined,
              maxOccurrences:
                recurring.endMode === 'occurrences' && recurring.maxOccurrences
                  ? Number(recurring.maxOccurrences)
                  : undefined
            }
          : undefined,
        planId: planId || undefined
      }

      const created = await meetingService.createMeetingRequest(data)

      showToast({ type: 'success', message: 'Meeting request sent' })
      onMeetingRequested(created)
      handleClose()
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to send meeting request'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (isSubmitting) return
    setStep(1)
    setRecipientId('')
    setType('regular_checkin')
    setPlatform('zoom')
    setMeetingUrl('')
    setDuration(30)
    setMessage('')
    setTimeSlots([{ start: '', end: '' }])
    setAgendaItems([{ title: '', description: '', estimatedMinutes: '' }])
    setActionItems([{ title: '', description: '', assignedTo: 'mentor', dueDate: '' }])
    setRecurring(DEFAULT_RECURRING_PATTERN)
    setErrors({})
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={handleClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Request a Meeting</h2>
              <p className="text-sm text-gray-500">Step {step} of 4</p>
            </div>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6">
            {step === 1 && (
              <div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Recipient *</label>
                  {loadingRelationships ? (
                    <div className="text-sm text-gray-500">Loading relationships...</div>
                  ) : relationships.length === 0 ? (
                    <div className="text-sm text-gray-500">No active relationships found</div>
                  ) : (
                    <select
                      value={recipientId}
                      onChange={e => setRecipientId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">Select a recipient</option>
                      {relationships.map(r => {
                        const counterpart = counterpartFor(r)
                        return (
                          <option key={r.id} value={counterpart?.id}>
                            {counterpart?.firstName} {counterpart?.lastName}
                          </option>
                        )
                      })}
                    </select>
                  )}
                  {errors.recipientId && <p className="text-sm text-red-500 mt-1">{errors.recipientId}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Type</label>
                    <select
                      value={type}
                      onChange={e => setType(e.target.value as MeetingType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {MEETING_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                    <select
                      value={platform}
                      onChange={e => setPlatform(e.target.value as MeetingPlatform)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      {MEETING_PLATFORMS.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meeting URL (optional)</label>
                  <input
                    type="text"
                    value={meetingUrl}
                    onChange={e => setMeetingUrl(e.target.value)}
                    placeholder="https://..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.meetingUrl && <p className="text-sm text-red-500 mt-1">{errors.meetingUrl}</p>}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (minutes) *</label>
                  <input
                    type="number"
                    value={duration}
                    onChange={e => setDuration(parseInt(e.target.value) || 0)}
                    min={5}
                    max={480}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  />
                  {errors.duration && <p className="text-sm text-red-500 mt-1">{errors.duration}</p>}
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message (optional)</label>
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 resize-none"
                  />
                </div>

                {plans.length > 0 && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Link to Plan (optional)
                    </label>
                    <select
                      value={planId}
                      onChange={e => setPlanId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="">No plan</option>
                      {plans.map(p => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {step === 2 && (
              <TimeSlotsPicker
                slots={timeSlots}
                onChange={setTimeSlots}
                error={errors.timeSlots}
                maxSlots={MAX_TIME_SLOTS}
              />
            )}

            {step === 3 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Agenda *</label>
                  <Button type="button" variant="secondary" size="sm" onClick={handleAddAgendaItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
                {agendaItems.map((item, index) => (
                  <Card key={index} className="mb-2">
                    <CardBody className="p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={item.title}
                            onChange={e => handleUpdateAgendaItem(index, 'title', e.target.value)}
                            placeholder="Agenda item title"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <div className="grid grid-cols-2 gap-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={e => handleUpdateAgendaItem(index, 'description', e.target.value)}
                              placeholder="Description (optional)"
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <input
                              type="number"
                              value={item.estimatedMinutes}
                              onChange={e =>
                                handleUpdateAgendaItem(index, 'estimatedMinutes', parseInt(e.target.value) || '')
                              }
                              placeholder="Minutes"
                              min={1}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                        </div>
                        {agendaItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveAgendaItem(index)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
                {errors.agenda && <p className="text-sm text-red-500 mt-1">{errors.agenda}</p>}
              </div>
            )}

            {step === 4 && (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Action Items *</label>
                  <Button type="button" variant="secondary" size="sm" onClick={handleAddActionItem}>
                    <Plus className="w-4 h-4 mr-1" />
                    Add Item
                  </Button>
                </div>
                {actionItems.map((item, index) => (
                  <Card key={index} className="mb-2">
                    <CardBody className="p-3">
                      <div className="flex items-start gap-2">
                        <div className="flex-1 space-y-2">
                          <input
                            type="text"
                            value={item.title}
                            onChange={e => handleUpdateActionItem(index, 'title', e.target.value)}
                            placeholder="Action item title"
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                          />
                          <div className="grid grid-cols-3 gap-2">
                            <input
                              type="text"
                              value={item.description}
                              onChange={e => handleUpdateActionItem(index, 'description', e.target.value)}
                              placeholder="Description (optional)"
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <select
                              value={item.assignedTo}
                              onChange={e => handleUpdateActionItem(index, 'assignedTo', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              <option value="mentor">Mentor</option>
                              <option value="mentee">Mentee</option>
                            </select>
                            <input
                              type="date"
                              value={item.dueDate}
                              onChange={e => handleUpdateActionItem(index, 'dueDate', e.target.value)}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                          </div>
                        </div>
                        {actionItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveActionItem(index)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                ))}
                {errors.actionItems && <p className="text-sm text-red-500 mt-1">{errors.actionItems}</p>}

                <RecurringPatternPicker value={recurring} onChange={setRecurring} error={errors.recurring} />
              </div>
            )}

            <div className="flex gap-3 justify-between border-t pt-4 mt-4">
              <div>
                {step > 1 && (
                  <Button type="button" variant="secondary" onClick={handleBack} disabled={isSubmitting}>
                    Back
                  </Button>
                )}
              </div>
              <div className="flex gap-3">
                <Button type="button" variant="secondary" onClick={handleClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                {step < 4 ? (
                  <Button type="button" variant="primary" onClick={handleNext}>
                    Next
                  </Button>
                ) : (
                  <Button type="submit" variant="primary" disabled={isSubmitting} isLoading={isSubmitting}>
                    Send Request
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default MeetingRequestModal
